import React, { useState, useEffect } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import { Toaster } from "sonner";
import { Button } from "@/components/ui/button";
import "@/index.css";
import "mapbox-gl/dist/mapbox-gl.css";

// Scroll to top on route change
function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

import HomePage from "./pages/HomePage";
import SearchResults from "./pages/SearchResults";
import PropertyDetail from "./pages/PropertyDetail";
import DeveloperProfile from "./pages/DeveloperProfile";
import DeveloperProfileEdit from "./pages/DeveloperProfileEdit";
import Developers from "./pages/Developers";
import AdminUsers from "./pages/AdminUsers";
import About from "./pages/About";
import Login from "./pages/Login";
import Register from "./pages/Register";
import ResetPassword from "./pages/ResetPassword";
import Dashboard from "./pages/Dashboard";
// BrokerDashboard ya no se usa - Todos usan Dashboard.js
import Favorites from "./pages/Favorites";
import AddProperty from "./pages/AddProperty";
import EditProperty from "./pages/EditProperty";
import Inquiries from "./pages/Inquiries";
import DevelopmentsPage from "./pages/DevelopmentsPage";
import DevelopmentDetail from "./pages/DevelopmentDetail";
import AddDevelopment from "./pages/AddDevelopment";
import BrokersDirectory from "./pages/BrokersDirectory";
import BrokerProfile from "./pages/BrokerProfile";
import Terms from "./pages/Terms";
import Privacy from "./pages/Privacy";
import MarketplaceInventory from "./pages/MarketplaceInventory";
import AccountSettings from "./pages/AccountSettings";
import AdminBrokers from "./pages/AdminBrokers";
import AdminSettings from "./pages/AdminSettings";
import BrokerProfileEdit from "./pages/BrokerProfileEdit";
import CreatePropertyRequest from "./pages/CreatePropertyRequest";
import PropertyRequests from "./pages/PropertyRequests";
import Pricing from "./pages/Pricing";
import NotFound from "./pages/NotFound";
import { API_BASE_URL } from "./config/api";

const AuthContext = React.createContext(null);

export const useAuth = () => {
  const context = React.useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showReloginBanner, setShowReloginBanner] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const malformedBrokersPaths = [
      "/ttps:/www.metalconcrete.com/brokers-directory",
      "/ttps://www.metalconcrete.com/brokers-directory",
    ];

    if (malformedBrokersPaths.some((p) => window.location.pathname.startsWith(p))) {
      window.location.replace("/brokers-directory");
    }
  }, []);

  const hydrateCurrentUser = async () => {
    try {
      const token =
        sessionStorage.getItem("token") || localStorage.getItem("token");
      const response = await fetch(`${API_BASE_URL}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        sessionStorage.setItem("user", JSON.stringify(data));
        setUser(data);
        if (data.needs_relogin) {
          setShowReloginBanner(true);
        }
      }
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    const token =
      sessionStorage.getItem("token") || localStorage.getItem("token");
    const userData =
      sessionStorage.getItem("user") || localStorage.getItem("user");

    if (token && userData) {
      const parsedUser = JSON.parse(userData);
      setUser(parsedUser);

      hydrateCurrentUser();
    }
    setLoading(false);
  }, []);

  const updateUser = (userData) => {
    sessionStorage.setItem("user", JSON.stringify(userData));
    setUser(userData);
  };

  const login = (token, userData) => {
    sessionStorage.setItem("token", token);
    sessionStorage.setItem("user", JSON.stringify(userData));
    setUser(userData);

    // Identificar usuario en Mixpanel - wrapped in try-catch to prevent tracking errors from breaking auth
    try {
      if (window.mixpanel) {
        window.mixpanel.identify(userData.email || userData._id);
        window.mixpanel.people.set({
          $email: userData.email,
          $name: userData.name,
          role: userData.role,
          subscription_plan: userData.subscription_plan || "free",
          $created: new Date().toISOString(),
        });
      }
    } catch (error) {
      console.warn("[Auth] Mixpanel tracking failed silently:", error);
      // Continue with auth flow even if Mixpanel fails
    }
  };

  const logout = () => {
    sessionStorage.removeItem("token");
    sessionStorage.removeItem("user");
    localStorage.removeItem("token"); // For backwards compatibility
    localStorage.removeItem("user");
    setUser(null);

    // Reset Mixpanel al hacer logout - wrapped in try-catch to prevent tracking errors
    try {
      if (window.mixpanel) {
        window.mixpanel.reset();
      }
    } catch (error) {
      console.warn("[Auth] Mixpanel reset failed silently:", error);
      // Continue with logout flow even if Mixpanel fails
    }

    window.location.href = "/";
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ user, login, logout, updateUser }}>
      <BrowserRouter>
        <ScrollToTop />
        <div className="App">
          <a href="#main-content" className="skip-link">
            Saltar al contenido principal
          </a>
          {showReloginBanner && (
            <div className="fixed top-0 left-0 right-0 z-50 bg-orange-500 text-white px-4 py-3 shadow-lg">
              <div className="max-w-7xl mx-auto flex items-center justify-between">
                <p className="text-sm font-medium">
                  ⚠️ Tu plan ha cambiado. Cierra sesión y vuelve a entrar para
                  ver los cambios.
                </p>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={logout}
                  className="bg-white text-orange-600 hover:bg-gray-100"
                >
                  Cerrar Sesión Ahora
                </Button>
              </div>
            </div>
          )}
          <main
            id="main-content"
            className={showReloginBanner ? "pt-14 md:pt-16" : ""}
            tabIndex="-1"
          >
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/search" element={<SearchResults />} />
              <Route path="/property/:id" element={<PropertyDetail />} />
              <Route path="/developer/:id" element={<DeveloperProfile />} />
              <Route
                path="/developer-profile/edit"
                element={
                  user ? <DeveloperProfileEdit /> : <Navigate to="/login" />
                }
              />
              <Route path="/developers" element={<Developers />} />
              <Route path="/developments-list" element={<DevelopmentsPage />} />
              <Route path="/development/:id" element={<DevelopmentDetail />} />
              <Route
                path="/add-development"
                element={user ? <AddDevelopment /> : <Navigate to="/login" />}
              />
              <Route path="/brokers-directory" element={<BrokersDirectory />} />
              <Route path="/broker/:id" element={<BrokerProfile />} />
              <Route
                path="/broker-profile/edit"
                element={
                  user?.role === "broker" ? (
                    <BrokerProfileEdit />
                  ) : (
                    <Navigate to="/dashboard" />
                  )
                }
              />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route
                path="/account"
                element={user ? <AccountSettings /> : <Navigate to="/login" />}
              />
              <Route
                path="/create-request"
                element={
                  user ? <CreatePropertyRequest /> : <Navigate to="/login" />
                }
              />
              <Route path="/property-requests" element={<PropertyRequests />} />
              <Route
                path="/inventario-brokers"
                element={
                  user ? <MarketplaceInventory /> : <Navigate to="/login" />
                }
              />
              <Route
                path="/admin/brokers"
                element={
                  user?.role === "admin" ? (
                    <AdminBrokers />
                  ) : (
                    <Navigate to="/dashboard" />
                  )
                }
              />
              <Route
                path="/admin/users"
                element={
                  user?.role === "admin" ? (
                    <AdminUsers />
                  ) : (
                    <Navigate to="/dashboard" />
                  )
                }
              />
              <Route
                path="/admin/settings"
                element={
                  user?.role === "admin" ? (
                    <AdminSettings />
                  ) : (
                    <Navigate to="/dashboard" />
                  )
                }
              />
              <Route path="/about" element={<About />} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route
                path="/dashboard"
                element={
                  user || localStorage.getItem("token") ? (
                    <Dashboard />
                  ) : (
                    <Navigate to="/login" />
                  )
                }
              />
              <Route
                path="/favorites"
                element={
                  user || localStorage.getItem("token") ? (
                    <Favorites />
                  ) : (
                    <Navigate to="/login" />
                  )
                }
              />
              <Route
                path="/inquiries"
                element={
                  user || localStorage.getItem("token") ? (
                    <Inquiries />
                  ) : (
                    <Navigate to="/login" />
                  )
                }
              />
              <Route
                path="/add-property"
                element={
                  user || localStorage.getItem("token") ? (
                    <AddProperty />
                  ) : (
                    <Navigate to="/login" />
                  )
                }
              />
              <Route
                path="/edit-property/:id"
                element={
                  user || localStorage.getItem("token") ? (
                    <EditProperty />
                  ) : (
                    <Navigate to="/login" />
                  )
                }
              />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </main>
          <Toaster position="top-right" />
        </div>
      </BrowserRouter>
    </AuthContext.Provider>
  );
}

export default App;
